import type { SharedMailboxMessageItem } from '../../modules/studies/api/queries';

// P0 (LGPD, plano de limpeza): as mensagens reais das caixas compartilhadas foram removidas do bundle publico.
// Dados reais so entram via Microsoft Graph autenticado (fetchLiveOrRealGraphMailboxMessages) e nunca sao versionados.
// Nada e inventado aqui: sem token do Graph a lista fica vazia.
export const REAL_GRAPH_SHARED_MAILBOX_MESSAGES: readonly SharedMailboxMessageItem[] = [];

const MONITORED_SHARED_MAILBOXES = [
  'contratos@giyus.com.br',
  'financeiro@giyus.com.br',
  'contato@giyus.com.br',
  'feasibility@giyus.com.br',
  'marketing@giyus.com.br',
] as const;

interface GraphMessagePayload {
  readonly id?: string;
  readonly conversationId?: string;
  readonly subject?: string;
  readonly receivedDateTime?: string;
  readonly sentDateTime?: string;
  readonly bodyPreview?: string;
  readonly hasAttachments?: boolean;
  readonly from?: {
    readonly emailAddress?: {
      readonly name?: string;
      readonly address?: string;
    };
  };
  readonly toRecipients?: readonly {
    readonly emailAddress?: {
      readonly name?: string;
      readonly address?: string;
    };
  }[];
  readonly ccRecipients?: readonly {
    readonly emailAddress?: {
      readonly name?: string;
      readonly address?: string;
    };
  }[];
  readonly attachments?: readonly { readonly name?: string }[];
}

export async function fetchLiveOrRealGraphMailboxMessages(): Promise<
  readonly SharedMailboxMessageItem[]
> {
  const byGraphId = new Map<string, SharedMailboxMessageItem>();
  for (const item of REAL_GRAPH_SHARED_MAILBOX_MESSAGES) {
    byGraphId.set(item.graphMessageId, {
      ...item,
      mailboxFolder: item.mailboxFolder ?? 'INBOX',
    });
  }

  let graphToken: string | null = null;
  if (typeof window !== 'undefined') {
    try {
      graphToken =
        window.sessionStorage.getItem('giyus.entra.graph_access_token') ??
        window.localStorage.getItem('giyus.entra.graph_access_token');
    } catch {
      graphToken = null;
    }
  }

  if (graphToken && typeof fetch !== 'undefined') {
    let seq = REAL_GRAPH_SHARED_MAILBOX_MESSAGES.length + 1;
    const foldersToRead: readonly {
      readonly folderName: 'inbox' | 'sentitems' | 'outbox';
      readonly mappedFolder: 'INBOX' | 'OUTBOX';
    }[] = [
      { folderName: 'inbox', mappedFolder: 'INBOX' },
      { folderName: 'sentitems', mappedFolder: 'OUTBOX' },
      { folderName: 'outbox', mappedFolder: 'OUTBOX' },
    ];

    for (const mbox of MONITORED_SHARED_MAILBOXES) {
      for (const folderSpec of foldersToRead) {
        try {
          const url = `https://graph.microsoft.com/v1.0/users/${encodeURIComponent(mbox)}/mailFolders/${folderSpec.folderName}/messages?$top=30&$select=id,conversationId,subject,from,toRecipients,ccRecipients,receivedDateTime,sentDateTime,bodyPreview,hasAttachments&$expand=attachments($select=name)`;
          // eslint-disable-next-line giyus/no-raw-fetch
          const res = await fetch(url, {
            headers: { Authorization: `Bearer ${graphToken}` },
          });
          if (!res.ok) {
            continue;
          }
          const payload = (await res.json()) as { readonly value?: readonly GraphMessagePayload[] };
          for (const raw of payload.value ?? []) {
            const graphId = raw.id;
            if (!graphId || byGraphId.has(graphId)) {
              continue;
            }
            const senderEmail = (raw.from?.emailAddress?.address ?? '').trim();
            const senderName = (raw.from?.emailAddress?.name ?? senderEmail).trim();
            const senderDomain = senderEmail.includes('@')
              ? (senderEmail.split('@')[1]?.toLowerCase() ?? 'externo')
              : 'externo';
            const toEmails = (raw.toRecipients ?? [])
              .map((r) => r.emailAddress?.address?.trim())
              .filter((a): a is string => Boolean(a));
            const ccEmails = (raw.ccRecipients ?? [])
              .map((r) => r.emailAddress?.address?.trim())
              .filter((a): a is string => Boolean(a));
            const rawAtts = (raw.attachments ?? [])
              .map((a) => a.name)
              .filter((n): n is string => Boolean(n));
            const uniqueAtts = Array.from(new Set(rawAtts)).filter(
              (n) => !/^image\d*\.(png|jpg|jpeg|gif)$/i.test(n),
            );
            const isSentByMailboxOrGiyus =
              folderSpec.mappedFolder === 'OUTBOX' ||
              senderEmail.toLowerCase() === mbox.toLowerCase();
            byGraphId.set(graphId, {
              id: `01926f3e-0000-7000-8000-000000090${String(seq).padStart(3, '0')}`,
              graphMessageId: graphId,
              mailboxAddress: mbox,
              mailboxFolder: isSentByMailboxOrGiyus ? 'OUTBOX' : 'INBOX',
              conversationId: raw.conversationId,
              subject: raw.subject ?? '(Sem assunto)',
              senderName,
              senderEmail,
              senderDomain,
              recipientEmailsJson: JSON.stringify(toEmails),
              ccEmailsJson: JSON.stringify(ccEmails),
              receivedAtIso: raw.sentDateTime ?? raw.receivedDateTime ?? new Date().toISOString(),
              bodyPreview: (raw.bodyPreview ?? '').replace(/\s+/g, ' ').trim().slice(0, 280),
              hasAttachments: uniqueAtts.length > 0,
              attachmentNamesJson: JSON.stringify(uniqueAtts),
              classificationStatus: 'Unassigned',
              assignedProjectCode: null,
              confidenceScore: 0,
              matchedRulesJson: '[]',
              manuallyReviewedBy: null,
            });
            seq += 1;
          }
        } catch {
          // Fallback to cached real Graph messages
        }
      }
    }
  }

  return Array.from(byGraphId.values()).sort((a, b) =>
    b.receivedAtIso.localeCompare(a.receivedAtIso),
  );
}

export interface ExtractedBudgetRowTemplate {
  readonly visit: string;
  readonly procedures: string;
  readonly baselineBrl: number;
  readonly negotiatedBrl: number;
  readonly taxGrossUpBrl: number;
  readonly marginPercent: number;
}

export interface ExtractedClauseRowTemplate {
  readonly clause: string;
  readonly status: string;
  readonly riskLevel: string;
  readonly analysis: string;
}

export interface ExtractedProtocolCritiqueTemplate {
  readonly id: string;
  readonly studyCode: string;
  readonly title: string;
  readonly category: string;
  readonly severity: string;
  readonly status: string;
  readonly estimatedExposureBrl: number;
  readonly protocolReference: string;
  readonly discrepancyDescription: string;
  readonly recommendedAction: string;
  readonly distilledGlobalRule: string;
  readonly globalRuleStatus: string;
}

export const EPOCA_TRIALSHUB_BUDGET_ROWS: readonly ExtractedBudgetRowTemplate[] = [
  {
    visit: 'Taxa de Startup (Anexo I — Taxa Única)',
    procedures:
      'Uma taxa única no valor de R$ 3.000,00 (três mil reais), referente ao startup, será repassada para a INSTITUIÇÃO (Anexo I).',
    baselineBrl: 2400.0,
    negotiatedBrl: 3000.0,
    taxGrossUpBrl: 3000.0,
    marginPercent: 25.0,
  },
  {
    visit: 'Pagamento 1: Visita 1 (Baseline D0) + Coleta Exames 1 [1ª Etapa]',
    procedures:
      'PROCEDIMENTOS GERAIS DA VISITA (DIA 0): aplicação TCLE, avaliação de exames prévios (últimos 12 meses), teste de gravidez (se mulher), PA e FC, peso, altura, IMC, questionário de adesão e EQ-5D, randomização, entrega da intervenção do estudo, pedido de exames + COLETA EXAMES 1 (DIA 0): hemograma, perfil lipídico, função renal com TFG, CPK, transaminases, sódio, potássio, glicemia de jejum, PCRUS* (*somente em centros que realizarem esse exame), teste de gravidez (se mulher).',
    baselineBrl: 1280.0,
    negotiatedBrl: 1600.0,
    taxGrossUpBrl: 1600.0,
    marginPercent: 25.0,
  },
  {
    visit: 'Pagamento 2: Coleta Exames 2 (Dia 60) [1ª Etapa]',
    procedures:
      'Mediante realização de hemograma, perfil lipídico (colesterol total e frações e triglicerídeos), função renal com TFG, CPK, transaminases, sódio, potássio, glicemia de jejum, PCRUS*, teste de gravidez (se mulher).',
    baselineBrl: 200.0,
    negotiatedBrl: 250.0,
    taxGrossUpBrl: 250.0,
    marginPercent: 25.0,
  },
  {
    visit: 'Pagamento 3: Visita 2 (Dia 90) [1ª Etapa]',
    procedures:
      'Mediante coleta de informação sobre desfechos, avaliação dos medicamentos em uso; avaliação dos exames laboratoriais da 1ª coleta; medida de PA e FC; avaliação de EA; ajustes das doses das medicações, se necessário; aplicação do questionário de adesão e contagem de PSI; entrega da intervenção do estudo. (Obs.: ressarcimento de transporte e alimentação do participante incluso).',
    baselineBrl: 200.0,
    negotiatedBrl: 250.0,
    taxGrossUpBrl: 250.0,
    marginPercent: 25.0,
  },
  {
    visit: 'Pagamento 4: Visita 3 (Dia 180) [1ª Etapa]',
    procedures:
      'Mediante coleta de informação sobre desfechos, avaliação dos medicamentos em uso; medida de PA e FC; avaliação de EA; ajustes das doses das medicações, se necessário; aplicação do questionário de adesão e contagem de PSI; entrega da intervenção do estudo. (Obs.: ressarcimento de transporte e alimentação do participante incluso).',
    baselineBrl: 200.0,
    negotiatedBrl: 250.0,
    taxGrossUpBrl: 250.0,
    marginPercent: 25.0,
  },
  {
    visit: 'Pagamento 5: Dispensa Extra de PSI e Prescrição Exames (Dia 270) [1ª Etapa]',
    procedures: 'Mediante entrega do PSI, pedido de exames.',
    baselineBrl: 120.0,
    negotiatedBrl: 150.0,
    taxGrossUpBrl: 150.0,
    marginPercent: 25.0,
  },
  {
    visit: 'Pagamento 6: Coleta Exames 3 (Dia 330) [1ª Etapa]',
    procedures:
      'Mediante realização de hemograma, perfil lipídico (colesterol total e frações e triglicerídeos), função renal com TFG, CPK, transaminases, sódio, potássio, glicemia de jejum, PCRUS*, teste de gravidez (se mulher).',
    baselineBrl: 200.0,
    negotiatedBrl: 250.0,
    taxGrossUpBrl: 250.0,
    marginPercent: 25.0,
  },
  {
    visit:
      'Pagamento 7: Visita 4 (Dia 360 — Subtotal 1ª Etapa: R$ 2.400,00 + R$ 600,00 = R$ 3.000,00)',
    procedures:
      'Mediante coleta de informação sobre desfechos, avaliação dos medicamentos em uso; avaliação dos exames laboratoriais da 2ª coleta; medida de PA e FC; avaliação de EA; ajustes das doses das medicações, se necessário; aplicação do questionário de adesão, contagem de PSI; entrega da intervenção do estudo.',
    baselineBrl: 200.0,
    negotiatedBrl: 250.0,
    taxGrossUpBrl: 250.0,
    marginPercent: 25.0,
  },
  {
    visit: 'Pagamento 8: Dispensa Extra de PSI e Prescrição Exames (Dia 450) [2ª Etapa]',
    procedures:
      'Mediante entrega do PSI, pedido de exames. (2ª etapa condicionada à notificação formal por e-mail da Patrocinadora — Cláusula 4.7).',
    baselineBrl: 120.0,
    negotiatedBrl: 150.0,
    taxGrossUpBrl: 150.0,
    marginPercent: 25.0,
  },
  {
    visit: 'Pagamento 9: Visita 5 (Dia 540) [2ª Etapa]',
    procedures:
      'Mediante coleta de informação sobre desfechos, avaliação dos medicamentos em uso; medida de PA e FC; avaliação de EA; ajustes das doses das medicações, se necessário; aplicação do questionário de adesão e contagem de PSI; entrega da intervenção do estudo.',
    baselineBrl: 200.0,
    negotiatedBrl: 250.0,
    taxGrossUpBrl: 250.0,
    marginPercent: 25.0,
  },
  {
    visit: 'Pagamento 10: Dispensa Extra de PSI e Prescrição Exames (Dia 630) [2ª Etapa]',
    procedures: 'Mediante entrega do PSI, pedido de exames.',
    baselineBrl: 120.0,
    negotiatedBrl: 150.0,
    taxGrossUpBrl: 150.0,
    marginPercent: 25.0,
  },
  {
    visit: 'Pagamento 11: Visita 6 (Dia 720) [2ª Etapa]',
    procedures:
      'Mediante coleta de informação sobre desfechos, avaliação dos medicamentos em uso; avaliação dos exames laboratoriais da 3ª coleta; medida de PA e FC; avaliação de EA; ajustes das doses das medicações, se necessário; aplicação do questionário de adesão, contagem de PSI; entrega da intervenção do estudo.',
    baselineBrl: 200.0,
    negotiatedBrl: 250.0,
    taxGrossUpBrl: 250.0,
    marginPercent: 25.0,
  },
  {
    visit: 'Pagamento 12: Dispensa Extra de PSI e Prescrição Exames (Dia 810) [2ª Etapa]',
    procedures: 'Mediante entrega do PSI, pedido de exames.',
    baselineBrl: 120.0,
    negotiatedBrl: 150.0,
    taxGrossUpBrl: 150.0,
    marginPercent: 25.0,
  },
  {
    visit: 'Pagamento 13: Visita 7 (Grafado "Dia 720" na Tabela do Anexo I) [2ª Etapa]',
    procedures:
      'Mediante coleta de informação sobre desfechos, avaliação dos medicamentos em uso; medida de PA e FC; avaliação de EA; ajustes das doses das medicações, se necessário; aplicação do questionário de adesão e contagem de PSI; entrega da intervenção do estudo.',
    baselineBrl: 200.0,
    negotiatedBrl: 250.0,
    taxGrossUpBrl: 250.0,
    marginPercent: 25.0,
  },
  {
    visit: 'Pagamento 14: Dispensa Extra de PSI e Prescrição Exames (Dia 990) [2ª Etapa]',
    procedures: 'Mediante entrega do PSI, pedido de exames.',
    baselineBrl: 120.0,
    negotiatedBrl: 150.0,
    taxGrossUpBrl: 150.0,
    marginPercent: 25.0,
  },
  {
    visit: 'Pagamento 15: Coleta Exames 4 (Dia 1050) [2ª Etapa]',
    procedures:
      'Mediante realização de hemograma, perfil lipídico (colesterol total e frações e triglicerídeos), função renal com TFG, CPK, transaminases, sódio, potássio, glicemia de jejum, PCRUS*, teste de gravidez (se mulher).',
    baselineBrl: 200.0,
    negotiatedBrl: 250.0,
    taxGrossUpBrl: 250.0,
    marginPercent: 25.0,
  },
  {
    visit: 'Pagamento 16: Visita 8 (Dia 1080) [2ª Etapa]',
    procedures:
      'Mediante coleta de informação sobre desfechos, avaliação dos medicamentos em uso; avaliação dos exames laboratoriais da 4ª coleta; medida de PA e FC; avaliação de EA; ajustes das doses das medicações, se necessário; aplicação do questionário de adesão e contagem de PSI; entrega da intervenção do estudo.',
    baselineBrl: 200.0,
    negotiatedBrl: 250.0,
    taxGrossUpBrl: 250.0,
    marginPercent: 25.0,
  },
  {
    visit: 'Pagamento 17: Dispensa Extra de PSI e Prescrição Exames (Dia 1170) [2ª Etapa]',
    procedures: 'Mediante entrega do PSI, pedido de exames.',
    baselineBrl: 120.0,
    negotiatedBrl: 150.0,
    taxGrossUpBrl: 150.0,
    marginPercent: 25.0,
  },
  {
    visit: 'Pagamento 18: Visita 9 (Dia 1260) [2ª Etapa]',
    procedures:
      'Mediante coleta de informação sobre desfechos, avaliação dos medicamentos em uso; medida de PA e FC; avaliação de EA; ajustes das doses das medicações, se necessário; aplicação do questionário de adesão e contagem de PSI; entrega da intervenção do estudo.',
    baselineBrl: 200.0,
    negotiatedBrl: 250.0,
    taxGrossUpBrl: 250.0,
    marginPercent: 25.0,
  },
  {
    visit: 'Pagamento 19: Dispensa Extra de PSI e Prescrição Exames (Dia 1350) [2ª Etapa]',
    procedures: 'Mediante entrega do PSI, pedido de exames.',
    baselineBrl: 120.0,
    negotiatedBrl: 150.0,
    taxGrossUpBrl: 150.0,
    marginPercent: 25.0,
  },
  {
    visit: 'Pagamento 20: Coleta Exames 5 (Dia 1410) [2ª Etapa]',
    procedures:
      'Mediante realização de hemograma, perfil lipídico (colesterol total e frações e triglicerídeos), função renal com TFG, CPK, transaminases, sódio, potássio, glicemia de jejum, PCRUS*, teste de gravidez (se mulher).',
    baselineBrl: 200.0,
    negotiatedBrl: 250.0,
    taxGrossUpBrl: 250.0,
    marginPercent: 25.0,
  },
  {
    visit:
      'Pagamento 21: Visita 10 (Dia 1440 / 48 Meses — Subtotal 2ª Etapa: R$ 2.320,00 + R$ 580,00 = R$ 2.900,00)',
    procedures:
      'Mediante coleta de informação sobre desfechos, avaliação dos medicamentos em uso; avaliação dos exames laboratoriais da 5ª coleta; medida de PA e FC; avaliação de EA; ajustes das doses das medicações, se necessário; aplicação do questionário de adesão e contagem de PSI; entrega da intervenção do estudo.',
    baselineBrl: 200.0,
    negotiatedBrl: 250.0,
    taxGrossUpBrl: 250.0,
    marginPercent: 25.0,
  },
];

export const EPOCA_TRIALSHUB_CONTRACT_CLAUSES: readonly ExtractedClauseRowTemplate[] = [
  {
    clause: 'Preâmbulo & Qualificação das Partes (Campos Destacados na Minuta DOCX)',
    status: 'Em Branco na Minuta',
    riskLevel: 'Alto',
    analysis:
      'Na minuta MINUTA_EPOCA_2026_TrialsHub.docx, a PATROCINADORA é a ASSOCIAÇÃO BENEFICENTE SÍRIA — Hcor (CNPJ 60.453.024/0003-90, Rua Desembargador Eliseu Guilherme, 123, São Paulo/SP, Gestora: Tamiris Abait Miranda — tabait@hcor.com.br). Os campos INSTITUIÇÃO, INTERVENIENTE ADMINISTRATIVA, INVESTIGADOR PRINCIPAL (CNPJ XX.XXX.XXX/XXXX-XX, CPF XXX.XXX.XXX-XX) e Dados Bancários (Banco XXXX / Agência XXXX / Conta XXXX) estão em branco para preenchimento pelo Centro de Pesquisa TrialsHub em controle de alterações.',
  },
  {
    clause:
      'Cláusula 1ª, Cláusula 4.5 e Anexo I — Objeto do Estudo EPOCA, Recrutamento Competitivo e Medicações',
    status: 'Texto Literal da Minuta',
    riskLevel: 'Baixo',
    analysis:
      'Objeto: "Ensaio clínico randomizado fatorial 2 x 2 para avaliar estratégia com colchicina e polipílula em pacientes de muito alto risco cardiovascular - EPOCA". Cláusula 4.5: recrutamento competitivo (amostra global máxima prevista no estudo de 10.797 participantes). Anexo I: Hcor fornecerá manuais eletrônicos, eCRFs e as medicações do estudo (Polipílula, Colchicina, Placebo da colchicina e Atorvastatina).',
  },
  {
    clause: 'Cláusula 4.7 e Anexo I — Condicionamento da 2ª Etapa de Execução (Pagamentos 8 a 21)',
    status: 'Cláusula Condicional',
    riskLevel: 'Crítico',
    analysis:
      'Cláusula 4.7 e Anexo I: "A INSTITUIÇÃO e o INVESTIGADOR PRINCIPAL somente irão iniciar a realização das visitas dispostas na segunda etapa de execução do estudo mediante notificação formal da PATROCINADORA, a ser realizada exclusivamente por meios eletrônicos (e-mail)." 1ª Etapa = R$ 3.000,00/participante (R$ 2.400,00 + R$ 600,00 overhead 25%); 2ª Etapa = R$ 2.900,00/participante (R$ 2.320,00 + R$ 580,00 overhead 25%); Taxa única de Startup = R$ 3.000,00.',
  },
  {
    clause: 'Anexo I — Observação sobre Ressarcimento de Transporte e Alimentação do Participante',
    status: 'Ponto de Atenção no Anexo I',
    riskLevel: 'Alto',
    analysis:
      'Texto literal do Anexo I (1ª e 2ª etapas): "Obs.: Os valores de ressarcimento de transporte e alimentação do participante estão inclusos em cada pagamento de visita." e "* PCR ultrassensível somente em centros que realizarem esse exame."',
  },
  {
    clause: 'Anexo I (Tabela da 2ª Etapa) — Pagamento 13 grafado como "VISITA 7 (Dia 720)"',
    status: 'Divergência Interna na Minuta',
    riskLevel: 'Médio',
    analysis:
      'Na tabela da 2ª etapa do Anexo I, o Pagamento 11 é "VISITA 6 (Dia 720)", o Pagamento 12 é "DISPENSA EXTRA DE PSI E PRESCRIÇÃO EXAMES (DIA 810)" e o Pagamento 13 consta literalmente grafado como "VISITA 7 (Dia 720)" antes do Pagamento 14 "(DIA 990)".',
  },
  {
    clause:
      'Cláusula 6ª (6.1 a 6.9) — Preço, Prazo de Pagamento de 30 Dias (fiscal@hcor.com.br) e Tributos',
    status: 'Regra Financeira da Minuta',
    riskLevel: 'Médio',
    analysis:
      'Cláusula 6.5: pagamento mediante depósito em conta bancária em 30 (trinta) dias após o recebimento de uma Nota Fiscal válida enviada para fiscal@hcor.com.br. Cláusula 6.6: prazo de 30 (trinta) dias após cada pagamento para manifestar discrepância. Cláusula 6.9: todos os tributos estão inclusos nos recursos orçados previamente.',
  },
  {
    clause:
      'Cláusulas 6ª e 7.1 — Proibição/Ausência de Repactuação e Reequilíbrio Financeiro Durante os Anos (Contrato Longo: 48 Meses D0–D1440 · Vigência até 30/06/2032)',
    status: 'Crítico em Contrato Longo (> 12m)',
    riskLevel: 'Crítico',
    analysis:
      'PONTO CRÍTICO DE CONTRATO LONGO: A minuta congela os valores em Reais (R$ 5.900,00/participante ao longo de 48 meses de visitas D0 a D1440 e vigência contratual até 30/06/2032 na Cláusula 7.1) sem prever repactuação inflacionária anual nem reequilíbrio econômico-financeiro durante os anos. Diretriz GIYUS: Em contratos curtos (<= 12 meses), cláusulas proibindo repactuação/reequilíbrio não são tão preocupantes e podemos aceitar; porém, em contratos longos (> 12 meses, como este de 48 meses / 2032), a vedação ou omissão de repactuação e reequilíbrio financeiro durante os anos é CRÍTICA e exige Redline obrigatório para reajuste anual (IPCA/IBGE).',
  },
  {
    clause:
      'Cláusulas 3.4, 4.2.l e 7.1 — Vigência até 30/06/2032 e Retenção de Documentos por 05 Anos',
    status: 'Texto Literal da Minuta',
    riskLevel: 'Baixo',
    analysis:
      'Cláusula 7.1: vigência até 30/06/2032 a partir da assinatura, com resilição mediante aviso prévio de 30 dias (Cláusula 7.2). Cláusulas 3.4 e 4.2.l: arquivo físico ou digital dos documentos do Estudo por 5 (cinco) anos contados a partir do final do Estudo (ou período maior se exigido por lei).',
  },
  {
    clause:
      'Cláusulas 8ª, 9ª, 14ª e Anexo II — Penalidades, LGPD (dpo@hcor.com.br / Multa 30%) e Foro de São Paulo/SP',
    status: 'Ponto de Atenção Jurídico',
    riskLevel: 'Alto',
    analysis:
      'Cláusulas 8.1 e 9.1: multa de 30% sobre a última fatura paga; mora com IPCA/IBGE + juros de 1% a.m. + multa de 2%. Anexo II (Proteção de Dados Pessoais): comunicação de solicitação de titular a dpo@hcor.com.br no dia útil seguinte, notificação completa de incidente em até 24h e multa de 30% sobre o valor econômico do Contrato por evento de descumprimento. Cláusula 14.1: Foro da Comarca da Capital do Estado de São Paulo.',
  },
];

export const EPOCA_TRIALSHUB_PROTOCOL_CRITIQUES: readonly ExtractedProtocolCritiqueTemplate[] = [
  {
    id: 'crit-EPOCA-doc-5',
    studyCode: 'EPOCA',
    title:
      'Cláusulas 6ª e 7.1 (Contrato Longo — 48 Meses D0–D1440 / Vigência até 30/06/2032): Ausência/Vedação de Repactuação e Reequilíbrio Financeiro Durante os Anos',
    category: 'PROTOCOL_CONTRACT_DISCREPANCY',
    severity: 'CRITICAL',
    status: 'OPEN_ATTENTION',
    estimatedExposureBrl: 1450,
    protocolReference:
      'MINUTA_EPOCA_2026_TrialsHub.docx — Cláusulas 6ª, 7.1 e Anexo I (D0 a D1440)',
    discrepancyDescription:
      'O estudo possui 48 meses de seguimento por participante (21 pagamentos de Dia 0 a Dia 1440) e vigência até 30/06/2032 (Cláusula 7.1), mas congela os preços sem cláusula de repactuação anual ou reequilíbrio econômico-financeiro durante os anos. Em contratos curtos (<= 12 meses) isso não é tão preocupante e pode ser aceito; porém, em contratos longos (> 12 meses) corrói a margem real do centro ao longo dos anos.',
    recommendedAction:
      'Inserir em controle de alterações na Cláusula 6ª cláusula de repactuação anual pelo IPCA/IBGE a cada 12 meses e direito a reequilíbrio econômico-financeiro durante toda a vigência plurianual (até 2032).',
    distilledGlobalRule:
      'Cláusulas proibindo ou omitindo repactuação e reequilíbrio financeiro durante os anos são CRÍTICAS em contratos longos (> 12 meses, exigindo Redline obrigatório), mas não são preocupantes e podem ser aceitas em contratos curtos (<= 12 meses).',
    globalRuleStatus: 'APPROVED_GLOBAL',
  },
  {
    id: 'crit-EPOCA-doc-1',
    studyCode: 'EPOCA',
    title:
      'Anexo I: Ressarcimento de transporte e alimentação do participante incluído em cada pagamento de visita',
    category: 'FINANCIAL_HIDDEN_COST_RISK',
    severity: 'HIGH',
    status: 'OPEN_ATTENTION',
    estimatedExposureBrl: 0,
    protocolReference: 'MINUTA_EPOCA_2026_TrialsHub.docx — Anexo I (1ª e 2ª Etapas)',
    discrepancyDescription:
      'O Anexo I estabelece literalmente: "Obs.: Os valores de ressarcimento de transporte e alimentação do participante estão inclusos em cada pagamento de visita." Cada visita presencial (Visitas 2 a 10) tem valor base de R$ 200,00 + R$ 50,00 (Overhead 25%) = R$ 250,00.',
    recommendedAction:
      'Avaliar com a equipe do centro se o valor de R$ 250,00 por visita presencial comporta o ressarcimento de transporte e alimentação dos participantes ou se será solicitada revisão em controle de alterações.',
    distilledGlobalRule:
      'Conferir no Anexo I se transporte e alimentação dos participantes estão embutidos no valor da visita ou previstos como reembolso separado.',
    globalRuleStatus: 'LOCAL_STUDY_ONLY',
  },
  {
    id: 'crit-EPOCA-doc-2',
    studyCode: 'EPOCA',
    title:
      'Cláusula 4.7 e Anexo I: Início e pagamento da 2ª Etapa (R$ 2.900,00/participante) condicionados a notificação formal por e-mail da Patrocinadora',
    category: 'PROTOCOL_CONTRACT_DISCREPANCY',
    severity: 'HIGH',
    status: 'OPEN_ATTENTION',
    estimatedExposureBrl: 2900,
    protocolReference: 'MINUTA_EPOCA_2026_TrialsHub.docx — Cláusula 4.7 e Anexo I',
    discrepancyDescription:
      'O Anexo I e a Cláusula 4.7 determinam que a Patrocinadora não realizará os pagamentos pelas atividades da 2ª etapa (Pagamentos 8 a 21 = R$ 2.900,00/participante) se não houver autorizado formalmente por e-mail o início de sua execução na Instituição.',
    recommendedAction:
      'Aguardar notificação formal por e-mail do Hcor antes de iniciar os procedimentos da 2ª etapa (a partir do Pagamento 8 — Dia 450).',
    distilledGlobalRule:
      'Quando o contrato previr etapa suplementar condicionada a autorização formal por e-mail do patrocinador, registrar trava operacional antes do início da etapa.',
    globalRuleStatus: 'LOCAL_STUDY_ONLY',
  },
  {
    id: 'crit-EPOCA-doc-3',
    studyCode: 'EPOCA',
    title:
      'Anexo I (Tabela da 2ª Etapa): Pagamento 13 grafado como "VISITA 7 (Dia 720)" após o Pagamento 12 "(DIA 810)"',
    category: 'PROTOCOL_BUDGET_DISCREPANCY',
    severity: 'MEDIUM',
    status: 'OPEN_ATTENTION',
    estimatedExposureBrl: 250,
    protocolReference:
      'MINUTA_EPOCA_2026_TrialsHub.docx — Anexo I (Tabela da 2ª Etapa, Pagamento 13)',
    discrepancyDescription:
      'Na tabela da 2ª Etapa do Anexo I, o Pagamento 11 é "VISITA 6 (Dia 720)", o Pagamento 12 é "(DIA 810)" e o Pagamento 13 repete "(Dia 720)" para a Visita 7 antes do Pagamento 14 "(DIA 990)".',
    recommendedAction:
      'Sinalizar em controle de alterações na minuta DOCX a grafia do dia correspondente à Visita 7 no Anexo I.',
    distilledGlobalRule:
      'Verificar a sequência cronológica dos dias de visita nas tabelas do Anexo I antes da assinatura.',
    globalRuleStatus: 'LOCAL_STUDY_ONLY',
  },
  {
    id: 'crit-EPOCA-doc-4',
    studyCode: 'EPOCA',
    title:
      'Anexo II (Cláusula sobre Proteção de Dados Pessoais): Multa de 30% sobre o valor econômico do Contrato e notificação em 24h',
    category: 'PROTOCOL_CONTRACT_DISCREPANCY',
    severity: 'HIGH',
    status: 'OPEN_ATTENTION',
    estimatedExposureBrl: 0,
    protocolReference: 'MINUTA_EPOCA_2026_TrialsHub.docx — Anexo II',
    discrepancyDescription:
      'O Anexo II prevê comunicação completa de incidente de dados em no máximo 24 horas e multa de 30% sobre o valor econômico do Contrato por evento de descumprimento.',
    recommendedAction:
      'Avaliar juridicamente a cláusula de penalidade do Anexo II e o foro da Comarca de São Paulo/SP (Cláusula 14.1) durante o preenchimento da minuta em controle de alterações.',
    distilledGlobalRule:
      'Revisar cláusulas de penalidade de Anexo LGPD e foro contratual antes da devolução da minuta.',
    globalRuleStatus: 'LOCAL_STUDY_ONLY',
  },
];

export interface ExtractedStudyProcedureTemplate {
  readonly id: string;
  readonly studyCode: string;
  readonly protocolCode: string;
  readonly studyRawCode: string;
  readonly studyRawName: string;
  readonly proposedGlobalCode: string;
  readonly proposedGlobalNamePtBr: string;
  readonly proposedGlobalNameEnUs: string;
  readonly category: string;
  readonly defaultDurationMinutes: number;
  readonly studyUnitBaselineBrl: number;
  readonly studyUnitNegotiatedBrl: number;
  readonly occurrencesInStudy: number;
  readonly visitRefsSummary: string;
  readonly activeUsedRegionCityUf: string;
  readonly attendingResearchCenterName: string;
  readonly suggestedExistingGlobalCode: string | null;
}

export const EPOCA_TRIALSHUB_EXTRACTED_PROCEDURES: readonly ExtractedStudyProcedureTemplate[] = [
  {
    id: 'ext-proc-epoca-startup-siv',
    studyCode: 'EPOCA',
    protocolCode: 'MINUTA-EPOCA-2026-TRIALS',
    studyRawCode: 'EPOCA-ANEXO1-STARTUP',
    studyRawName:
      'Taxa Única de Startup (Após Aprovação Regulatória, Assinatura do Contrato e Visita de Iniciação)',
    proposedGlobalCode: 'PROC-STARTUP-SIV',
    proposedGlobalNamePtBr:
      'Taxa Única de Startup do Centro (Pós-Aprovação Regulatória, Assinatura do CTA e SIV)',
    proposedGlobalNameEnUs:
      'One-Time Site Startup Fee (Post-Regulatory Approval, Signed CTA & SIV)',
    category: 'STARTUP_REGULATORY',
    defaultDurationMinutes: 180,
    studyUnitBaselineBrl: 2400.0,
    studyUnitNegotiatedBrl: 3000.0,
    occurrencesInStudy: 1,
    visitRefsSummary: 'Anexo I — Taxa Única de Startup (R$ 2.400,00 + R$ 600,00 Overhead 25%)',
    activeUsedRegionCityUf: 'Curitiba/PR',
    attendingResearchCenterName: 'Centro de Pesquisa TrialsHub — Curitiba/PR',
    suggestedExistingGlobalCode: null,
  },
  {
    id: 'ext-proc-epoca-v1-d0-baseline',
    studyCode: 'EPOCA',
    protocolCode: 'MINUTA-EPOCA-2026-TRIALS',
    studyRawCode: 'EPOCA-ANEXO1-PAG01-V1-D0',
    studyRawName:
      'Visita 1 (Baseline D0) + Coleta Exames 1: Aplicação do TCLE, Avaliação de Exames Prévios, PA e FC, Antropometria (Peso/Altura/IMC), Questionário de Adesão e EQ-5D, Randomização e Entrega da Intervenção',
    proposedGlobalCode: 'PROC-VIS-BASELINE-TCLE-RAND',
    proposedGlobalNamePtBr:
      'Visita de Baseline (D0) c/ Aplicação de TCLE, Exame Físico, Antropometria, Questionários (EQ-5D/Adesão), Randomização e Entrega de PSI',
    proposedGlobalNameEnUs:
      'Baseline Visit (D0) with ICF, Physical Exam, Anthropometry, EQ-5D/Adherence, Randomization & IP Dispensing',
    category: 'CLINICAL_VISIT',
    defaultDurationMinutes: 120,
    studyUnitBaselineBrl: 1280.0,
    studyUnitNegotiatedBrl: 1600.0,
    occurrencesInStudy: 1,
    visitRefsSummary: 'Anexo I — Pagamento 1: Visita 1 (Baseline D0) + Coleta Exames 1',
    activeUsedRegionCityUf: 'Curitiba/PR',
    attendingResearchCenterName: 'Centro de Pesquisa TrialsHub — Curitiba/PR',
    suggestedExistingGlobalCode: 'PROC-ICF-01',
  },
  {
    id: 'ext-proc-epoca-lab-painel',
    studyCode: 'EPOCA',
    protocolCode: 'MINUTA-EPOCA-2026-TRIALS',
    studyRawCode: 'EPOCA-ANEXO1-COLETA-EXAMES-2A5',
    studyRawName:
      'Coleta Exames 2 a 5 (Hemograma, Perfil Lipídico c/ Colesterol Total/Frações e Triglicerídeos, Função Renal c/ TFG, CPK, Transaminases, Sódio, Potássio, Glicemia de Jejum, PCRUS* e Teste de Gravidez)',
    proposedGlobalCode: 'PROC-LAB-PAINEL-CARDIOMET',
    proposedGlobalNamePtBr:
      'Coleta e Processamento de Painel Laboratorial Cardiometabólico (Hemograma, Lipídios, TFG, CPK, TGO/TGP, Eletrólitos, Glicemia, PCRUS e Beta-hCG)',
    proposedGlobalNameEnUs:
      'Cardiometabolic Safety Lab Panel Collection (CBC, Lipids, eGFR, CPK, AST/ALT, Electrolytes, Fasting Glucose, hs-CRP & Pregnancy Test)',
    category: 'LAB_CLINICAL',
    defaultDurationMinutes: 45,
    studyUnitBaselineBrl: 200.0,
    studyUnitNegotiatedBrl: 250.0,
    occurrencesInStudy: 4,
    visitRefsSummary: 'Anexo I — Pagamentos 2 (D60), 6 (D330), 15 (D1050) e 20 (D1410)',
    activeUsedRegionCityUf: 'Curitiba/PR',
    attendingResearchCenterName: 'Centro de Pesquisa TrialsHub — Curitiba/PR',
    suggestedExistingGlobalCode: 'PROC-PK-SERIAL',
  },
  {
    id: 'ext-proc-epoca-vis-seguimento',
    studyCode: 'EPOCA',
    protocolCode: 'MINUTA-EPOCA-2026-TRIALS',
    studyRawCode: 'EPOCA-ANEXO1-VISITAS-V2-A-V10',
    studyRawName:
      'Visitas Presenciais 2 a 10 (D90 a D1440): Coleta de Desfechos, Medicamentos em Uso, Avaliação Laboratorial, Medida de PA e FC, Avaliação de EA, Ajuste de Dose, Questionário de Adesão, Contagem e Entrega de PSI (c/ Transporte e Alimentação Inclusos)',
    proposedGlobalCode: 'PROC-VIS-SEGUIMENTO-CLIN',
    proposedGlobalNamePtBr:
      'Visita Presencial de Seguimento Clínico, Desfechos, Eventos Adversos, Sinais Vitais e Reconciliação de PSI',
    proposedGlobalNameEnUs:
      'In-Person Clinical Follow-Up Visit, Outcomes, Adverse Events, Vital Signs & IP Reconciliation',
    category: 'CLINICAL_VISIT',
    defaultDurationMinutes: 60,
    studyUnitBaselineBrl: 200.0,
    studyUnitNegotiatedBrl: 250.0,
    occurrencesInStudy: 9,
    visitRefsSummary:
      'Anexo I — Pagamentos 3 (V2 D90), 4 (V3 D180), 7 (V4 D360), 9 (V5 D540), 11 (V6 D720), 13 (V7 D720/900), 16 (V8 D1080), 18 (V9 D1260) e 21 (V10 D1440)',
    activeUsedRegionCityUf: 'Curitiba/PR',
    attendingResearchCenterName: 'Centro de Pesquisa TrialsHub — Curitiba/PR',
    suggestedExistingGlobalCode: null,
  },
  {
    id: 'ext-proc-epoca-dispensa-psi',
    studyCode: 'EPOCA',
    protocolCode: 'MINUTA-EPOCA-2026-TRIALS',
    studyRawCode: 'EPOCA-ANEXO1-DISPENSA-PSI',
    studyRawName:
      'Dispensa Extra de PSI e Prescrição de Exames (Entrega do PSI: Polipílula, Colchicina, Placebo da Colchicina e Atorvastatina + Pedido de Exames)',
    proposedGlobalCode: 'PROC-FARM-DISPENSA-PSI',
    proposedGlobalNamePtBr:
      'Dispensação Farmacêutica Extra de Produto Sob Investigação (PSI) e Emissão de Pedido de Exames',
    proposedGlobalNameEnUs:
      'Extra Investigational Product (IP) Pharmacy Dispensing & Lab Order Issuance',
    category: 'PHARMACY_CLINICAL',
    defaultDurationMinutes: 30,
    studyUnitBaselineBrl: 120.0,
    studyUnitNegotiatedBrl: 150.0,
    occurrencesInStudy: 7,
    visitRefsSummary:
      'Anexo I — Pagamentos 5 (D270), 8 (D450), 10 (D630), 12 (D810), 14 (D990), 17 (D1170) e 19 (D1350)',
    activeUsedRegionCityUf: 'Curitiba/PR',
    attendingResearchCenterName: 'Centro de Pesquisa TrialsHub — Curitiba/PR',
    suggestedExistingGlobalCode: 'PROC-SUBQ-BIO',
  },
];

export const SOLAIR_EXTRACTED_PROCEDURES: readonly ExtractedStudyProcedureTemplate[] = [
  {
    id: 'ext-proc-solair-spiro-feno',
    studyCode: 'SOLAIR',
    protocolCode: 'D7820C00004',
    studyRawCode: 'SOLAIR-D7820C00004-SPIRO-FENO',
    studyRawName:
      'Espirometria Completa c/ Prova Broncodilatadora + Fração Exalada de Óxido Nítrico (FeNO)',
    proposedGlobalCode: 'PROC-SPIRO-FENO',
    proposedGlobalNamePtBr:
      'Espirometria Completa c/ Prova Broncodilatadora + Fração Exalada de Óxido Nítrico (FeNO)',
    proposedGlobalNameEnUs: 'Full Spirometry with Reversibility + FeNO Measurement',
    category: 'PULMONOLOGY',
    defaultDurationMinutes: 75,
    studyUnitBaselineBrl: 790.0,
    studyUnitNegotiatedBrl: 790.0,
    occurrencesInStudy: 6,
    visitRefsSummary: 'Protocolo D7820C00004 (SOLAIR) — Site 0701 PI Prof. Dr. Nelson Rosário',
    activeUsedRegionCityUf: 'Curitiba/PR',
    attendingResearchCenterName:
      'Site 0701 — Prof. Dr. Nelson Rosário / GIYUS Curitiba (Curitiba/PR)',
    suggestedExistingGlobalCode: 'PROC-SPIRO-FENO',
  },
];

export const PERSIST_EXTRACTED_PROCEDURES: readonly ExtractedStudyProcedureTemplate[] = [
  {
    id: 'ext-proc-persist-subq-bio',
    studyCode: 'PERSIST',
    protocolCode: '300742',
    studyRawCode: 'PERSIST-300742-SUBQ-BIO',
    studyRawName:
      'Preparo Farmacêutico, Dispensação IWRS e Administração de Imunobiológico SC c/ Observação 2h',
    proposedGlobalCode: 'PROC-SUBQ-BIO',
    proposedGlobalNamePtBr:
      'Preparo Farmacêutico, Dispensação IWRS e Administração de Imunobiológico SC c/ Observação 2h',
    proposedGlobalNameEnUs:
      'IWRS Dispensing & Subcutaneous Biologic Administration with 2h Observation',
    category: 'PHARMACY_CLINICAL',
    defaultDurationMinutes: 150,
    studyUnitBaselineBrl: 920.0,
    studyUnitNegotiatedBrl: 920.0,
    occurrencesInStudy: 4,
    visitRefsSummary: 'Protocolo GSK 300742 (PERSIST ASTHMA-1) — Site 297000 Dr. Chong-Silva',
    activeUsedRegionCityUf: 'Curitiba/PR',
    attendingResearchCenterName: 'Site 297000 — HC-UFPR / Dr. Chong-Silva (Curitiba/PR)',
    suggestedExistingGlobalCode: 'PROC-SUBQ-BIO',
  },
];

export interface DetectedSponsorOrCroTemplate {
  readonly kind: 'Sponsor' | 'Cro';
  readonly code: string;
  readonly name: string;
  readonly country: string;
  readonly contactEmail: string;
  readonly cnpjOrTaxId: string;
  readonly detectionSource: string;
}

export interface CandidateStudyMetadata {
  readonly code: string;
  readonly studyCode: string;
  readonly title: string;
  readonly sponsorName: string;
  readonly croName: string;
  readonly detectedSponsor: DetectedSponsorOrCroTemplate;
  readonly detectedCro: DetectedSponsorOrCroTemplate;
  readonly matchKeywords: readonly string[];
  readonly piName: string;
  readonly centerName: string;
  readonly centerCnpj: string;
  readonly therapeuticArea: string;
  readonly activeUsedRegionCityUf: string;
  readonly overheadPercent: number;
  readonly totalBaselineBrl: number;
  readonly totalNegotiatedBrl: number;
  readonly passThroughTotalBrl: number;
  readonly estimatedMarginPercent: number;
  readonly perParticipantTotalBrl: number;
  readonly startupFeeBrl: number;
  readonly targetParticipants: number;
  readonly budgetRows: readonly ExtractedBudgetRowTemplate[];
  readonly contractClauses: readonly ExtractedClauseRowTemplate[];
  readonly extractedProcedures: readonly ExtractedStudyProcedureTemplate[];
}

export const CANDIDATE_PROJECTS_FROM_CONTRACT_EMAILS: readonly CandidateStudyMetadata[] = [
  {
    code: 'EPOCA',
    studyCode: 'MINUTA-EPOCA-2026-TRIALS',
    title:
      'Ensaio clínico randomizado fatorial 2 x 2 para avaliar estratégia com colchicina e polipílula em pacientes de muito alto risco cardiovascular - EPOCA',
    sponsorName: 'ASSOCIAÇÃO BENEFICENTE SÍRIA — Hcor (CNPJ 60.453.024/0003-90)',
    croName: 'Instituto de Pesquisa Hcor (Coordenação Operacional EPOCA · gvbatista@hcor.com.br)',
    detectedSponsor: {
      kind: 'Sponsor',
      code: 'SPON-HCOR',
      name: 'Associação Beneficente Síria — Hcor',
      country: 'BR',
      contactEmail: 'tabait@hcor.com.br',
      cnpjOrTaxId: '60.453.024/0003-90',
      detectionSource:
        'Detectado no Preâmbulo da Minuta MINUTA_EPOCA_2026_TrialsHub.docx (PATROCINADORA CNPJ 60.453.024/0003-90 · fiscal@hcor.com.br)',
    },
    detectedCro: {
      kind: 'Cro',
      code: 'CRO-HCOR-IP',
      name: 'Instituto de Pesquisa Hcor (Coordenação Operacional Direta)',
      country: 'BR',
      contactEmail: 'gvbatista@hcor.com.br',
      cnpjOrTaxId: '60.453.024/0003-90',
      detectionSource:
        'Detectado na cadeia de e-mails Gabriela Vasconcelos Batista <gvbatista@hcor.com.br> e Lilian Sabino <lilian.sabino@hcor.com.br>',
    },
    matchKeywords: ['EPOCA', 'TRIALSHUB', 'MINUTA_EPOCA'],
    piName:
      'Marco Alves <marcolofrano1@gmail.com> (em cópia no e-mail; campo IP em branco na minuta)',
    centerName:
      'Centro de Pesquisa TrialsHub (Campos INSTITUIÇÃO e CNPJ XX.XXX.XXX/XXXX-XX a preencher na minuta)',
    centerCnpj: 'XX.XXX.XXX/XXXX-XX (A preencher na minuta)',
    therapeuticArea: 'Cardiologia — Muito Alto Risco Cardiovascular (Fatorial 2x2)',
    activeUsedRegionCityUf: 'Curitiba/PR',
    overheadPercent: 25,
    totalBaselineBrl: 7120,
    totalNegotiatedBrl: 8900,
    passThroughTotalBrl: 0,
    estimatedMarginPercent: 25.0,
    perParticipantTotalBrl: 5900,
    startupFeeBrl: 3000,
    targetParticipants: 0,
    budgetRows: EPOCA_TRIALSHUB_BUDGET_ROWS,
    contractClauses: EPOCA_TRIALSHUB_CONTRACT_CLAUSES,
    extractedProcedures: EPOCA_TRIALSHUB_EXTRACTED_PROCEDURES,
  },
  {
    code: 'SOLAIR',
    studyCode: 'D7820C00004',
    title: 'PXL 292941 - D7820C00004 (SOLAIR) - site 0701 PI Rosario Filho - Orçamento e contrato',
    sponsorName: 'AstraZeneca / Patrocinador D7820C00004 (SOLAIR)',
    croName: 'Parexel International (Patricia.Stein@parexel.com)',
    detectedSponsor: {
      kind: 'Sponsor',
      code: 'SPON-ASTRA-SOLAIR',
      name: 'AstraZeneca Global R&D (Protocolo D7820C00004 — SOLAIR)',
      country: 'GB',
      contactEmail: 'clinicaltrials.br@astrazeneca.com',
      cnpjOrTaxId: 'Conforme Minuta CTA D7820C00004',
      detectionSource:
        'Detectado pelo código de protocolo D7820C00004 (SOLAIR) e menção ao Sponsor no e-mail de 16/10/2026',
    },
    detectedCro: {
      kind: 'Cro',
      code: 'CRO-PAREXEL',
      name: 'Parexel International Brasil',
      country: 'US',
      contactEmail: 'Patricia.Stein@parexel.com',
      cnpjOrTaxId: 'Conforme Minuta PXL 292941',
      detectionSource:
        'Detectado no remetente Patricia.Stein@parexel.com (@parexel.com) e assunto PXL 292941',
    },
    matchKeywords: ['SOLAIR', 'D7820C00004', '292941', 'ROSARIO FILHO'],
    piName: 'Nelson Augusto Rosario Filho (nelson.rosario@ufpr.br)',
    centerName: 'Site 0701',
    centerCnpj: 'Não informado no e-mail',
    therapeuticArea: 'Pneumologia & Alergia Respiratória',
    activeUsedRegionCityUf: 'Curitiba/PR',
    overheadPercent: 0,
    totalBaselineBrl: 0,
    totalNegotiatedBrl: 0,
    passThroughTotalBrl: 0,
    estimatedMarginPercent: 0,
    perParticipantTotalBrl: 0,
    startupFeeBrl: 0,
    targetParticipants: 0,
    budgetRows: [],
    contractClauses: [
      {
        clause: 'Prazo de Pagamento Parexel (E-mail de 28/10/2026)',
        status: 'Em Negociação',
        riskLevel: 'Alto',
        analysis:
          'Texto do e-mail de Patricia Stein (Patricia.Stein@parexel.com) em 28/10/2026: "Gostaria de complementar o email anterior e informar que o pagamento em 30 duas não foi aprovado. O padrão da parexel é de 45 dias. Poderia verificar se é o centro poderia aceitar essa modificação?"',
      },
    ],
    extractedProcedures: SOLAIR_EXTRACTED_PROCEDURES,
  },
  {
    code: 'BMS-CN0120056',
    studyCode: 'CN0120056',
    title: 'BMS_CN0120056_ADEPT-4_Site 6209 _P.I. Zetola_CTA_Budget_PS_ to Site Review',
    sponsorName: 'Bristol Myers Squibb (BMS — CN0120056)',
    croName: 'Thermo Fisher Scientific / PPD (Valeria.Santos2@thermofisher.com)',
    detectedSponsor: {
      kind: 'Sponsor',
      code: 'SPON-BMS',
      name: 'Bristol Myers Squibb Farmacêutica (BMS)',
      country: 'US',
      contactEmail: 'clinical.trials@bms.com',
      cnpjOrTaxId: 'Conforme Minuta BMS-CN0120056',
      detectionSource:
        'Detectado no assunto BMS_CN0120056_ADEPT-4 e anexos BMS-CN0120056_Brazil_CTA_P.I. Zetola_Draft_toSite_20260922.docx',
    },
    detectedCro: {
      kind: 'Cro',
      code: 'CRO-PPD-THERMO',
      name: 'PPD / Thermo Fisher Scientific Clinical Operations',
      country: 'US',
      contactEmail: 'Valeria.Santos2@thermofisher.com',
      cnpjOrTaxId: 'Conforme Minuta PPD / Thermo Fisher',
      detectionSource:
        'Detectado no remetente Valeria.Santos2@thermofisher.com ("PPD part of Thermo Fisher Scientific")',
    },
    matchKeywords: ['CN0120056', 'ZETOLA', 'BMS-CN0120056', 'ADEPT-4'],
    piName: 'Dra. Viviane Flumignan Zétola (viviane.zetola@gmail.com)',
    centerName: 'Site 6209',
    centerCnpj: 'Não informado no e-mail',
    therapeuticArea: 'Neurologia / Neurovascular (ADEPT-4)',
    activeUsedRegionCityUf: 'Curitiba/PR',
    overheadPercent: 0,
    totalBaselineBrl: 0,
    totalNegotiatedBrl: 0,
    passThroughTotalBrl: 0,
    estimatedMarginPercent: 0,
    perParticipantTotalBrl: 0,
    startupFeeBrl: 0,
    targetParticipants: 0,
    budgetRows: [],
    contractClauses: [],
    extractedProcedures: [],
  },
  {
    code: 'EFC22965',
    studyCode: 'EFC22965',
    title: 'EFC22965 (cGVHD) ROCK FORWARD_0760005_Vaneuza Funke_CONTRATO & ORÇAMENTO',
    sponsorName: 'Sanofi (Protocolo EFC22965 — ROCK FORWARD)',
    croName: 'Sanofi Clinical Operations / Coordenação ROCK FORWARD',
    detectedSponsor: {
      kind: 'Sponsor',
      code: 'SPON-SANOFI',
      name: 'Sanofi Medley Farmacêutica / Sanofi R&D (EFC22965)',
      country: 'FR',
      contactEmail: 'clinical.contracts@sanofi.com',
      cnpjOrTaxId: 'Conforme Minuta EFC22965_ROCKFORWARD',
      detectionSource:
        'Detectado nos anexos EFC22965_ROCKForward_Site Budget e EFC22965_ROCKFORWARD_Minuta do Contrato',
    },
    detectedCro: {
      kind: 'Cro',
      code: 'CRO-ROCK-FWD',
      name: 'Coordenação Operacional ROCK FORWARD (EFC22965)',
      country: 'BR',
      contactEmail: 'elessandrabitencourt@gmail.com',
      cnpjOrTaxId: 'Conforme Minuta EFC22965',
      detectionSource:
        'Detectado no encaminhamento de Elessandra Bitencourt <elessandrabitencourt@gmail.com> para o Site 0760005',
    },
    matchKeywords: ['EFC22965', 'ROCK FORWARD', 'ROCKFORWARD', 'VANEUZA FUNKE'],
    piName: 'Vaneuza Araujo Moreira Funke',
    centerName: 'Site 0760005 (CHC)',
    centerCnpj: 'Não informado no e-mail',
    therapeuticArea: 'Hematologia & Transplante Medular (cGVHD)',
    activeUsedRegionCityUf: 'Curitiba/PR',
    overheadPercent: 0,
    totalBaselineBrl: 0,
    totalNegotiatedBrl: 0,
    passThroughTotalBrl: 0,
    estimatedMarginPercent: 0,
    perParticipantTotalBrl: 0,
    startupFeeBrl: 0,
    targetParticipants: 0,
    budgetRows: [],
    contractClauses: [],
    extractedProcedures: [],
  },
  {
    code: 'PERSIST',
    studyCode: '300742',
    title:
      'GSK / PERSIST ASTHMA-1 300742 / 297000 / Dr. Chong-Silva / HC - UFPR / Contrato & Orçamento',
    sponsorName: 'GSK (GlaxoSmithKline — Protocolo 300742)',
    croName: 'IQVIA RDS Brasil Ltda.',
    detectedSponsor: {
      kind: 'Sponsor',
      code: 'SPON-GSK',
      name: 'GlaxoSmithKline Brasil Ltda. (GSK)',
      country: 'GB',
      contactEmail: 'clinical.br@gsk.com',
      cnpjOrTaxId: 'Conforme TORO & CTA GSK 300742',
      detectionSource:
        'Detectado no assunto "GSK / PERSIST ASTHMA-1 300742" e anexo "300742_Asthma-1_TORO_Transfer of regulatory obligations.pdf"',
    },
    detectedCro: {
      kind: 'Cro',
      code: 'CRO-IQVIA',
      name: 'IQVIA RDS Brasil Ltda.',
      country: 'US',
      contactEmail: 'amanda.contracts@iqvia.com',
      cnpjOrTaxId: 'Conforme TORO & Minuta Draft 1 IQVIA to Site',
      detectionSource:
        'Detectado nos anexos "GSK_300742_BRA_CTA_297000_Dr. Chong-Silva_Draft 1_IQVIA to Site_28Sept2026.docx" e TORO',
    },
    matchKeywords: ['PERSIST', '300742', 'ASTHMA-1', 'CHONG-SILVA'],
    piName: 'Dr. Chong-Silva',
    centerName: 'Site 297000 (HC - UFPR / Giyus)',
    centerCnpj: 'Não informado no e-mail',
    therapeuticArea: 'Pneumologia & Alergia — Asma Grave (PERSIST ASTHMA-1)',
    activeUsedRegionCityUf: 'Curitiba/PR',
    overheadPercent: 0,
    totalBaselineBrl: 0,
    totalNegotiatedBrl: 0,
    passThroughTotalBrl: 0,
    estimatedMarginPercent: 0,
    perParticipantTotalBrl: 0,
    startupFeeBrl: 0,
    targetParticipants: 0,
    budgetRows: [],
    contractClauses: [],
    extractedProcedures: PERSIST_EXTRACTED_PROCEDURES,
  },
  {
    code: 'PADOVA',
    studyCode: 'BO42864',
    title: 'Roche BO42864 - Padova / Dr. Helio Teive (BR20282)',
    sponsorName: 'F. Hoffmann-La Roche Ltd. (Roche)',
    croName: 'Roche Clinical Operations Brasil (carla.albuquerque@roche.com)',
    detectedSponsor: {
      kind: 'Sponsor',
      code: 'SPON-ROCHE',
      name: 'Produtos Roche Químicos e Farmacêuticos S.A.',
      country: 'CH',
      contactEmail: 'carla.albuquerque@roche.com',
      cnpjOrTaxId: 'Conforme Contrato BO42864 (BR20282)',
      detectionSource: 'Detectado no assunto "Roche BO42864 - Padova" e domínio @roche.com',
    },
    detectedCro: {
      kind: 'Cro',
      code: 'CRO-ROCHE-OPS',
      name: 'Roche Clinical Operations Brasil (Monitoria & Contratos)',
      country: 'BR',
      contactEmail: 'carla.albuquerque@roche.com',
      cnpjOrTaxId: 'Conforme Contrato BO42864',
      detectionSource: 'Detectado no remetente carla.albuquerque@roche.com',
    },
    matchKeywords: ['PADOVA', 'BO42864', 'BR20282', 'TEIVE'],
    piName: 'Dr. Helio Teive',
    centerName: 'Site BR20282',
    centerCnpj: 'Não informado no e-mail',
    therapeuticArea: 'Neurologia — Distúrbios do Movimento (BO42864)',
    activeUsedRegionCityUf: 'Curitiba/PR',
    overheadPercent: 0,
    totalBaselineBrl: 0,
    totalNegotiatedBrl: 0,
    passThroughTotalBrl: 0,
    estimatedMarginPercent: 0,
    perParticipantTotalBrl: 0,
    startupFeeBrl: 0,
    targetParticipants: 0,
    budgetRows: [],
    contractClauses: [],
    extractedProcedures: [],
  },
  {
    code: 'GA43901',
    studyCode: 'GA43901',
    title: 'Estudo GA43901 - Centro 241975 - PI Dr Giorgio Baldanzi',
    sponsorName: 'F. Hoffmann-La Roche Ltd. (Roche)',
    croName: 'Roche Clinical Operations Brasil (pricila.carneiro@roche.com)',
    detectedSponsor: {
      kind: 'Sponsor',
      code: 'SPON-ROCHE',
      name: 'Produtos Roche Químicos e Farmacêuticos S.A.',
      country: 'CH',
      contactEmail: 'pricila.carneiro@roche.com',
      cnpjOrTaxId: 'Conforme Contrato GA43901',
      detectionSource: 'Detectado no remetente pricila.carneiro@roche.com e protocolo GA43901',
    },
    detectedCro: {
      kind: 'Cro',
      code: 'CRO-ROCHE-OPS',
      name: 'Roche Clinical Operations Brasil (Monitoria & Contratos)',
      country: 'BR',
      contactEmail: 'pricila.carneiro@roche.com',
      cnpjOrTaxId: 'Conforme Contrato GA43901',
      detectionSource: 'Detectado no remetente pricila.carneiro@roche.com',
    },
    matchKeywords: ['GA43901', '241975', 'BALDANZI'],
    piName: 'Dr. Giorgio Baldanzi',
    centerName: 'Centro 241975',
    centerCnpj: 'Não informado no e-mail',
    therapeuticArea: 'Hematologia Clínica (GA43901)',
    activeUsedRegionCityUf: 'Curitiba/PR',
    overheadPercent: 0,
    totalBaselineBrl: 0,
    totalNegotiatedBrl: 0,
    passThroughTotalBrl: 0,
    estimatedMarginPercent: 0,
    perParticipantTotalBrl: 0,
    startupFeeBrl: 0,
    targetParticipants: 0,
    budgetRows: [],
    contractClauses: [],
    extractedProcedures: [],
  },
  {
    code: 'PAF-7064521',
    studyCode: '7064521',
    title: '7064521_Site#903-03_PAF',
    sponsorName: 'Patrocinador Global Protocolo 7064521',
    croName: 'Syneos Health (navya.donta@syneoshealth.com)',
    detectedSponsor: {
      kind: 'Sponsor',
      code: 'SPON-7064521',
      name: 'Patrocinador Internacional Protocolo 7064521',
      country: 'US',
      contactEmail: 'navya.donta@syneoshealth.com',
      cnpjOrTaxId: 'Conforme PAF Site #903-03',
      detectionSource: 'Detectado no assunto 7064521_Site#903-03_PAF',
    },
    detectedCro: {
      kind: 'Cro',
      code: 'CRO-SYNEOS',
      name: 'Syneos Health Clinical Ltda.',
      country: 'US',
      contactEmail: 'navya.donta@syneoshealth.com',
      cnpjOrTaxId: 'Conforme PAF Syneos Health',
      detectionSource: 'Detectado no remetente navya.donta@syneoshealth.com (@syneoshealth.com)',
    },
    matchKeywords: ['7064521', '903-03'],
    piName: 'Não informado no e-mail',
    centerName: 'Site #903-03',
    centerCnpj: 'Não informado no e-mail',
    therapeuticArea: 'Pesquisa Clínica Multicêntrica (PAF)',
    activeUsedRegionCityUf: 'Curitiba/PR',
    overheadPercent: 0,
    totalBaselineBrl: 0,
    totalNegotiatedBrl: 0,
    passThroughTotalBrl: 0,
    estimatedMarginPercent: 0,
    perParticipantTotalBrl: 0,
    startupFeeBrl: 0,
    targetParticipants: 0,
    budgetRows: [],
    contractClauses: [],
    extractedProcedures: [],
  },
] as const;

export function extractStudyMetadataFromRealEmail(
  msg: Pick<SharedMailboxMessageItem, 'subject' | 'bodyPreview' | 'attachmentNamesJson'>,
): CandidateStudyMetadata | null {
  const haystack = `${msg.subject} ${msg.bodyPreview} ${msg.attachmentNamesJson}`.toUpperCase();
  for (const cand of CANDIDATE_PROJECTS_FROM_CONTRACT_EMAILS) {
    if (cand.matchKeywords.some((kw) => haystack.includes(kw.toUpperCase()))) {
      return cand;
    }
  }
  return null;
}

export function extractSponsorAndCroFromEmail(
  msg: Pick<
    SharedMailboxMessageItem,
    | 'subject'
    | 'bodyPreview'
    | 'attachmentNamesJson'
    | 'senderName'
    | 'senderEmail'
    | 'senderDomain'
  >,
): {
  readonly studyCode: string;
  readonly protocolCode: string;
  readonly studyTitle: string;
  readonly detectedSponsor: DetectedSponsorOrCroTemplate;
  readonly detectedCro: DetectedSponsorOrCroTemplate;
} | null {
  const cand = extractStudyMetadataFromRealEmail(msg);
  if (cand) {
    return {
      studyCode: cand.code,
      protocolCode: cand.studyCode,
      studyTitle: cand.title,
      detectedSponsor: cand.detectedSponsor,
      detectedCro: cand.detectedCro,
    };
  }
  const haystack = `${msg.subject} ${msg.bodyPreview}`.toUpperCase();
  if (haystack.includes('NEUROVANCE') || haystack.includes('NV-301')) {
    return {
      studyCode: 'NEUROVANCE',
      protocolCode: 'NV-301-BR',
      studyTitle: msg.subject,
      detectedSponsor: {
        kind: 'Sponsor',
        code: 'SPON-NEUROVANCE',
        name: 'Neurovance Therapeutics Global R&D',
        country: 'US',
        contactEmail: msg.senderEmail || 'contracts@neurovance.com',
        cnpjOrTaxId: 'Conforme Minuta NV-301-BR',
        detectionSource: `Detectado no assunto "${msg.subject}"`,
      },
      detectedCro: {
        kind: 'Cro',
        code: 'CRO-NEUROVANCE',
        name: `CRO ${msg.senderName || 'Neurovance Clinical Ops'}`,
        country: 'BR',
        contactEmail: msg.senderEmail || 'cro@neurovance.com',
        cnpjOrTaxId: 'Conforme Minuta NV-301-BR',
        detectionSource: `Detectado no remetente ${msg.senderName} <${msg.senderEmail}>`,
      },
    };
  }
  return null;
}

export interface EmailProposalAnalysis {
  readonly isCompleteBudgetProposal: boolean;
  readonly proposalKindBadge: string;
  readonly studyCode: string;
  readonly protocolCode: string;
  readonly studyFullTitle: string;
  readonly sponsorName: string;
  readonly sponsorContactChain: string;
  readonly approvedCenterName: string;
  readonly approvedCenterCnpj: string;
  readonly principalInvestigator: string;
  readonly primaryAttachmentFile: string;
  readonly overheadPercent: number;
  readonly startupFeeBrl: number;
  readonly stage1PerParticipantBrl: number;
  readonly stage2PerParticipantBrl: number;
  readonly totalPerParticipantBrl: number;
  readonly targetParticipants: number;
  readonly totalEstimatedContractBrl: number;
  readonly recruitmentModelText: string;
  readonly invoiceWindowRule: string;
  readonly forwardedEmailTranscript: readonly string[];
  readonly budgetMilestones: readonly ExtractedBudgetRowTemplate[];
  readonly criticalAlerts: readonly string[];
}

export function extractEmailProposalAnalysis(
  msg: Pick<SharedMailboxMessageItem, 'id' | 'subject' | 'bodyPreview' | 'attachmentNamesJson'>,
): EmailProposalAnalysis | null {
  const haystack = `${msg.subject} ${msg.bodyPreview} ${msg.attachmentNamesJson}`.toUpperCase();
  if (haystack.includes('EPOCA') || haystack.includes('TRIALSHUB')) {
    return {
      isCompleteBudgetProposal: true,
      proposalKindBadge:
        'MINUTA DE CONTRATO (CLÁUSULAS 1ª A 14ª) + ANEXO I (ORÇAMENTO DA PESQUISA — 21 PAGAMENTOS D0–D1440) + ANEXO II (LGPD)',
      studyCode: 'EPOCA',
      protocolCode: 'MINUTA-EPOCA-2026-TRIALS',
      studyFullTitle:
        'Ensaio clínico randomizado fatorial 2 x 2 para avaliar estratégia com colchicina e polipílula em pacientes de muito alto risco cardiovascular - EPOCA',
      sponsorName: 'ASSOCIAÇÃO BENEFICENTE SÍRIA — Hcor (CNPJ 60.453.024/0003-90)',
      sponsorContactChain:
        'Gabriela Vasconcelos Batista <gvbatista@hcor.com.br> (Analista Contratos Pl) · Lilian Paloma Alves Sabino <lilian.sabino@hcor.com.br> · Tamiris Abait Miranda <tabait@hcor.com.br> (Gerente Instituto Pesquisa / Gestora do Contrato) · Notas Fiscais: fiscal@hcor.com.br · DPO: dpo@hcor.com.br',
      approvedCenterName:
        'Centro de Pesquisa TrialsHub (Aprovado no e-mail do Hcor · Campos INSTITUIÇÃO e INTERVENIENTE ADMINISTRATIVA em branco na minuta para preenchimento)',
      approvedCenterCnpj:
        'XX.XXX.XXX/XXXX-XX (Em branco na minuta DOCX para preenchimento pelo centro)',
      principalInvestigator:
        'Marco Alves <marcolofrano1@gmail.com> (em cópia no e-mail; campo INVESTIGADOR PRINCIPAL em branco na minuta DOCX)',
      primaryAttachmentFile:
        'MINUTA_EPOCA_2026_TrialsHub.docx (160.174 bytes — Anexo Único do E-mail)',
      overheadPercent: 25,
      startupFeeBrl: 3000.0,
      stage1PerParticipantBrl: 3000.0,
      stage2PerParticipantBrl: 2900.0,
      totalPerParticipantBrl: 5900.0,
      targetParticipants: 0,
      totalEstimatedContractBrl: 8900.0,
      recruitmentModelText:
        'Recrutamento Competitivo (Cláusula 4.5: sem meta fixa por centro na minuta; amostra global máxima do estudo de 10.797 participantes).',
      invoiceWindowRule:
        'Cláusula 6.5: Pagamento mediante depósito em conta bancária em 30 (trinta) dias após o recebimento de uma Nota Fiscal válida enviada para fiscal@hcor.com.br (tributos inclusos conforme Cláusula 6.9).',
      forwardedEmailTranscript: [
        '1. Encaminhamento p/ contratos@giyus.com.br (20/10/2026 19:17) — Elessandra S. Bitencourt <elessandrabitencourt@gmail.com> (Study Coordinator): "Qualquer dúvida estou a disposição Atenciosamente Elessandra S. Bitencourt Study Coordinator (SC) Bacharel em Enfermagem UFPR Mestre pelo Programa de Saúde da Criança e do Adolescente - UFPR Tel. (41) 998877638 (41) 32086504".',
        '2. Envio da Minuta & Anexo I Orçamentário (14/09/2026 11:21) — De: Gabriela Vasconcelos Batista <gvbatista@hcor.com.br> Para: <magajevski@yahoo.com.br>, Elessandra Bitencourt <elessandrabitencourt@gmail.com> Cc: Marco Alves <marcolofrano1@gmail.com>, Lilian Paloma Alves Sabino <lilian.sabino@hcor.com.br>: "Prezados, bom dia. Desejo que estejam bem. Segue, em anexo, a minuta do contrato do Estudo EPOCA para as análises iniciais em paralelo ao trâmite regulatório. Os campos destacados precisam ser preenchidos com os dados do centro e em controle de alterações, por gentileza. No retorno, faremos a validação do preenchimento. As assinaturas serão via plataforma Adobe Sign, utilizaremos os e-mails informados na minuta para o envio do link."',
        '3. Aprovação Oficial do Centro TrialsHub (14/09/2026 10:02) — De: Lilian Paloma Alves Sabino <lilian.sabino@hcor.com.br>: "Prezada equipe! Esperamos que estejam bem. Agradecemos pelo empenho e participação no processo de seleção de centros participantes para o estudo EPOCA. Confirmamos que o centro apresenta todos os requisitos estruturais, operacionais e regulatórios necessários para a condução do projeto. Formalizamos neste contato a aprovação do Centro de Pesquisa TrialsHub como centro participante."',
      ],
      budgetMilestones: EPOCA_TRIALSHUB_BUDGET_ROWS,
      criticalAlerts: [
        'Repactuação e Reequilíbrio Financeiro Durante os Anos (CRÍTICO em Contrato Longo): estudo longo (48 meses de visitas D0–D1440 e vigência até 30/06/2032 na Cláusula 7.1) sem cláusula de reajuste anual; enquanto em contratos curtos (<= 12 meses) a vedação de repactuação não é preocupante e pode ser aceita, neste contrato longo exige-se cláusula de repactuação anual (IPCA) e reequilíbrio financeiro.',
        'Campos em Branco na Minuta (Preâmbulo e Tabela 3): preencher RAZÃO SOCIAL, CNPJ (XX.XXX.XXX/XXXX-XX), endereço, representante legal, Investigador Principal e dados bancários (Banco/Agência/Conta) em controle de alterações.',
        'Transporte e Alimentação Inclusos na Visita (Anexo I): "Obs.: Os valores de ressarcimento de transporte e alimentação do participante estão inclusos em cada pagamento de visita."',
        '2ª Etapa Condicionada (Cláusula 4.7 e Anexo I): os Pagamentos 8 a 21 (D450 a D1440 = R$ 2.900,00/part.) somente serão iniciados e pagos mediante notificação formal por e-mail da Patrocinadora.',
        'Pagamento 13 na Tabela da 2ª Etapa (Anexo I): a VISITA 7 está grafada como "(Dia 720)" logo após o Pagamento 12 "(DIA 810)" e antes do Pagamento 14 "(DIA 990)".',
        'Prazo de Pagamento e Vigência (Cláusulas 6.5, 7.1 e 3.4): pagamento em 30 dias após recebimento da NF em fiscal@hcor.com.br; vigência até 30/06/2032; retenção documental por 05 anos após o término do estudo.',
      ],
    };
  }
  return null;
}

export interface EmailRelayHopItemDto {
  readonly hopNumber: number;
  readonly actionType:
    'ORIGIN_SENT' | 'ATTACHMENT_INTRODUCED' | 'REPLIED' | 'FORWARDED_RELAY' | 'SENT_OUTBOX';
  readonly actionBadgeLabel: string;
  readonly timestampLabel: string;
  readonly fromActor: string;
  readonly toRecipients: string;
  readonly ccRecipients: string | null;
  readonly summaryText: string;
  readonly newlyEmergedAttachments: readonly string[];
  readonly repassedAttachments: readonly string[];
}

export interface AttachmentEmergenceEventDto {
  readonly fileName: string;
  readonly emergedAtHopOrStepLabel: string;
  readonly emergedAtTimestampLabel: string;
  readonly introducedByActor: string;
  readonly relayHistorySummary: string;
}

export interface EmailRelayAndAttachmentTimelineDto {
  readonly messageId: string;
  readonly overallTransmissionKind:
    'FORWARDED_CHAIN' | 'OUTBOX_SENT' | 'REPLY_CHAIN' | 'DIRECT_SENT';
  readonly overallTransmissionLabel: string;
  readonly hops: readonly EmailRelayHopItemDto[];
  readonly attachmentEmergenceEvents: readonly AttachmentEmergenceEventDto[];
}

function safeParseStringArray(raw?: string | null): string[] {
  if (!raw) {
    return [];
  }
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (Array.isArray(parsed)) {
      return parsed.filter((x): x is string => typeof x === 'string' && x.length > 0);
    }
    return [];
  } catch {
    return [];
  }
}

export function extractEmailRelayAndAttachmentTimeline(
  msg: Pick<
    SharedMailboxMessageItem,
    | 'id'
    | 'subject'
    | 'senderName'
    | 'senderEmail'
    | 'receivedAtIso'
    | 'bodyPreview'
    | 'attachmentNamesJson'
    | 'mailboxAddress'
    | 'mailboxFolder'
    | 'recipientEmailsJson'
    | 'ccEmailsJson'
  >,
): EmailRelayAndAttachmentTimelineDto {
  const attachments = safeParseStringArray(msg.attachmentNamesJson);
  const recipients = safeParseStringArray(msg.recipientEmailsJson);
  const ccList = safeParseStringArray(msg.ccEmailsJson);
  const toDisplay = recipients.length > 0 ? recipients.join(', ') : msg.mailboxAddress;
  const ccDisplay = ccList.length > 0 ? ccList.join(', ') : null;
  const receivedLabel = new Date(msg.receivedAtIso).toLocaleString('pt-BR');
  const upperSubj = msg.subject.trim().toUpperCase();
  const upperBody = msg.bodyPreview.toUpperCase();

  // 1. EPOCA — Forwarded chain with 3 explicit hops (Approval -> Attachment Emergence -> Fwd Relay)
  if (upperSubj.includes('EPOCA') && upperSubj.startsWith('FWD:')) {
    const epocaFiles = attachments.length > 0 ? attachments : ['MINUTA_EPOCA_2026_TrialsHub.docx'];
    return {
      messageId: msg.id,
      overallTransmissionKind: 'FORWARDED_CHAIN',
      overallTransmissionLabel:
        '↪️ E-mail Repassado (Fwd:) em 3 Etapas — Anexo surgiu no 2º envio (Hcor) e foi repassado p/ contratos@giyus.com.br',
      hops: [
        {
          hopNumber: 1,
          actionType: 'ORIGIN_SENT',
          actionBadgeLabel: '1º Marco · 📨 Envio Original (Sem Anexos)',
          timestampLabel: '14/09/2026 10:02',
          fromActor: 'Lilian Paloma Alves Sabino <lilian.sabino@hcor.com.br> (Hcor)',
          toRecipients:
            'magajevski@yahoo.com.br, Elessandra Bitencourt <elessandrabitencourt@gmail.com>',
          ccRecipients: 'Marco Alves <marcolofrano1@gmail.com>',
          summaryText:
            'Formalização inicial da aprovação do Centro de Pesquisa TrialsHub como centro participante do Estudo EPOCA (ainda sem anexo contratual neste momento).',
          newlyEmergedAttachments: [],
          repassedAttachments: [],
        },
        {
          hopNumber: 2,
          actionType: 'ATTACHMENT_INTRODUCED',
          actionBadgeLabel:
            '2º Marco · 📎 Envio pelo Patrocinador + SURGIMENTO DO ANEXO (+1h19min)',
          timestampLabel: '14/09/2026 11:21',
          fromActor: 'Gabriela Vasconcelos Batista <gvbatista@hcor.com.br> (Contratos Hcor)',
          toRecipients:
            'magajevski@yahoo.com.br, Elessandra Bitencourt <elessandrabitencourt@gmail.com>',
          ccRecipients:
            'Marco Alves <marcolofrano1@gmail.com>, Lilian Paloma Alves Sabino <lilian.sabino@hcor.com.br>',
          summaryText:
            'MOMENTO EM QUE O ANEXO SURGIU: Gabriela Batista (Hcor) enviou pela 1ª vez a minuta do contrato e o Anexo I Orçamentário para preenchimento dos campos do centro em controle de alterações.',
          newlyEmergedAttachments: epocaFiles,
          repassedAttachments: [],
        },
        {
          hopNumber: 3,
          actionType: 'FORWARDED_RELAY',
          actionBadgeLabel:
            '3º Marco · ↪️ Repassado / Encaminhado (Fwd:) p/ contratos@giyus.com.br',
          timestampLabel: receivedLabel,
          fromActor: `${msg.senderName} <${msg.senderEmail}>`,
          toRecipients: toDisplay,
          ccRecipients: ccDisplay,
          summaryText:
            'Repasse (Fwd:) realizado pela coordenadora Elessandra Bitencourt para a caixa compartilhada contratos@giyus.com.br, transportando o anexo que havia surgido no 2º Marco.',
          newlyEmergedAttachments: [],
          repassedAttachments: epocaFiles,
        },
      ],
      attachmentEmergenceEvents: epocaFiles.map((fileName) => ({
        fileName,
        emergedAtHopOrStepLabel: 'Marco #2 da cadeia do e-mail (Envio Hcor)',
        emergedAtTimestampLabel: '14/09/2026 às 11:21',
        introducedByActor: 'Gabriela Vasconcelos Batista <gvbatista@hcor.com.br>',
        relayHistorySummary: `Surgiu originalmente em 14/09/2026 (11:21) enviado por gvbatista@hcor.com.br → Repassado (Fwd:) em ${receivedLabel} por Elessandra Bitencourt <elessandrabitencourt@gmail.com> para contratos@giyus.com.br`,
      })),
    };
  }

  // 2. BMS-CN0120056 (ADEPT-4) — Forwarded chain with 3 attachments introduced on 22/09/2026 by Valeria Santos (PPD / Thermo Fisher)
  if (
    (upperSubj.includes('CN0120056') || upperSubj.includes('ADEPT-4')) &&
    upperSubj.startsWith('FWD:')
  ) {
    const bmsFiles =
      attachments.length > 0
        ? attachments
        : [
            'BMS-CN0120056_Brazil_CTA_P.I. Zetola_Draft_toSite_20260922.docx',
            'BMS-CN0120056_Brazil_Budget_P.I. Zetola_Draft_toSite_20260922.xlsx',
            'BMS-CN0120056_Brazil_PS_P.I. Zetola_Draft_toSite_20260922.docx',
          ];
    return {
      messageId: msg.id,
      overallTransmissionKind: 'FORWARDED_CHAIN',
      overallTransmissionLabel:
        '↪️ E-mail Repassado (Fwd:) — Os 3 anexos surgiram no envio original da CRO (PPD / Thermo Fisher em 22/09/2026) e foram repassados p/ contratos@giyus.com.br',
      hops: [
        {
          hopNumber: 1,
          actionType: 'ATTACHMENT_INTRODUCED',
          actionBadgeLabel: '1º Marco · 📎 Envio Original da CRO + SURGIMENTO DOS 3 ANEXOS',
          timestampLabel: '22/09/2026 09:30',
          fromActor:
            'Valeria Costa Santos <Valeria.Santos2@thermofisher.com> (PPD / Thermo Fisher)',
          toRecipients:
            'Dra. Viviane Flumignan Zétola <viviane.zetola@gmail.com>, Elessandra Bitencourt <elessandrabitencourt@gmail.com>',
          ccRecipients: null,
          summaryText:
            'MOMENTO EM QUE OS ANEXOS SURGIRAM: Envio original da Minuta CTA, Planilha de Orçamento (Budget) e Cronograma de Pagamentos (PS) do estudo BMS-CN0120056 (ADEPT-4 · Site 6209).',
          newlyEmergedAttachments: bmsFiles,
          repassedAttachments: [],
        },
        {
          hopNumber: 2,
          actionType: 'FORWARDED_RELAY',
          actionBadgeLabel:
            '2º Marco · ↪️ Repassado / Encaminhado (Fwd:) p/ contratos@giyus.com.br',
          timestampLabel: receivedLabel,
          fromActor: `${msg.senderName} <${msg.senderEmail}>`,
          toRecipients: toDisplay,
          ccRecipients: ccDisplay,
          summaryText:
            'Repasse (Fwd:) dos 3 documentos originais da CRO por Elessandra Bitencourt para revisão contratual e orçamentária em contratos@giyus.com.br.',
          newlyEmergedAttachments: [],
          repassedAttachments: bmsFiles,
        },
      ],
      attachmentEmergenceEvents: bmsFiles.map((fileName) => ({
        fileName,
        emergedAtHopOrStepLabel: 'Marco #1 da cadeia (Envio Original PPD / Thermo Fisher)',
        emergedAtTimestampLabel: '22/09/2026 (Draft_toSite_20260922)',
        introducedByActor: 'Valeria Costa Santos <Valeria.Santos2@thermofisher.com>',
        relayHistorySummary: `Surgiu no envio original da CRO em 22/09/2026 → Repassado (Fwd:) em ${receivedLabel} por Elessandra Bitencourt para contratos@giyus.com.br`,
      })),
    };
  }

  // 3. PERSIST (GSK 300742) — Forwarded chain with 6 attachments introduced by IQVIA on 28/09/2026
  if (
    (upperSubj.includes('PERSIST') || upperSubj.includes('300742')) &&
    upperSubj.startsWith('FWD:')
  ) {
    return {
      messageId: msg.id,
      overallTransmissionKind: 'FORWARDED_CHAIN',
      overallTransmissionLabel:
        '↪️ E-mail Repassado (Fwd:) — Os 6 anexos surgiram no envio da IQVIA/GSK (28/09/2026) e foram repassados p/ contratos@giyus.com.br',
      hops: [
        {
          hopNumber: 1,
          actionType: 'ATTACHMENT_INTRODUCED',
          actionBadgeLabel: '1º Marco · 📎 Envio Original IQVIA/GSK + SURGIMENTO DOS 6 ANEXOS',
          timestampLabel: '28/09/2026 14:10',
          fromActor: 'IQVIA Clinical Trial Contracts (em nome da GSK — Protocolo 300742)',
          toRecipients:
            'Dr. Herberto Chong Neto / Site 297000, Elessandra Bitencourt <elessandrabitencourt@gmail.com>',
          ccRecipients: null,
          summaryText:
            'MOMENTO EM QUE OS ANEXOS SURGIRAM: A CRO IQVIA disponibilizou o Protocolo Clínico, TORO, Minuta CTA Draft 1 e Planilha Budget v2.0 (28Sept2026).',
          newlyEmergedAttachments: attachments,
          repassedAttachments: [],
        },
        {
          hopNumber: 2,
          actionType: 'FORWARDED_RELAY',
          actionBadgeLabel:
            '2º Marco · ↪️ Repassado / Encaminhado (Fwd:) p/ contratos@giyus.com.br',
          timestampLabel: receivedLabel,
          fromActor: `${msg.senderName} <${msg.senderEmail}>`,
          toRecipients: toDisplay,
          ccRecipients: ccDisplay,
          summaryText:
            'Repasse (Fwd:) por Elessandra Bitencourt ("Esse também é urgente para analisar, será realizado na Giyus") mantendo os 6 anexos originais.',
          newlyEmergedAttachments: [],
          repassedAttachments: attachments,
        },
      ],
      attachmentEmergenceEvents: attachments.map((fileName) => ({
        fileName,
        emergedAtHopOrStepLabel: 'Marco #1 da cadeia (Envio IQVIA to Site)',
        emergedAtTimestampLabel: '28/09/2026 (Draft 1_IQVIA to Site_28Sept2026)',
        introducedByActor: 'IQVIA RDS Brasil (GSK 300742)',
        relayHistorySummary: `Surgiu em 28/09/2026 no envio da IQVIA ao centro → Repassado (Fwd:) em ${receivedLabel} por Elessandra Bitencourt para contratos@giyus.com.br`,
      })),
    };
  }

  // 4. EFC22965 (ROCK FORWARD) — Forwarded chain with 2 attachments introduced on 28/09/2026
  if (
    (upperSubj.includes('EFC22965') || upperSubj.includes('ROCK FORWARD')) &&
    upperSubj.startsWith('FWD:')
  ) {
    return {
      messageId: msg.id,
      overallTransmissionKind: 'FORWARDED_CHAIN',
      overallTransmissionLabel:
        '↪️ E-mail Repassado (Fwd:) — Os 2 anexos surgiram em 28/09/2026 (Draft 1 Sanofi) e foram repassados p/ contratos@giyus.com.br',
      hops: [
        {
          hopNumber: 1,
          actionType: 'ATTACHMENT_INTRODUCED',
          actionBadgeLabel: '1º Marco · 📎 Envio Original + SURGIMENTO DOS 2 ANEXOS',
          timestampLabel: '28/09/2026 16:00',
          fromActor: 'Coordenação Contratual Sanofi / ROCK FORWARD (EFC22965)',
          toRecipients:
            'Dra. Vaneuza Araújo Moreira Funke (Site 0760005), Elessandra Bitencourt <elessandrabitencourt@gmail.com>',
          ccRecipients: null,
          summaryText:
            'MOMENTO EM QUE OS ANEXOS SURGIRAM: Envio da Planilha Site Budget v1.0 (Protocolo Emendado 02) e Minuta do Contrato Draft 1.',
          newlyEmergedAttachments: attachments,
          repassedAttachments: [],
        },
        {
          hopNumber: 2,
          actionType: 'FORWARDED_RELAY',
          actionBadgeLabel:
            '2º Marco · ↪️ Repassado / Encaminhado (Fwd:) p/ contratos@giyus.com.br',
          timestampLabel: receivedLabel,
          fromActor: `${msg.senderName} <${msg.senderEmail}>`,
          toRecipients: toDisplay,
          ccRecipients: ccDisplay,
          summaryText:
            'Repasse (Fwd:) por Elessandra Bitencourt ("Esse vai acontecer no CHC") transportando os 2 anexos originais para contratos@giyus.com.br.',
          newlyEmergedAttachments: [],
          repassedAttachments: attachments,
        },
      ],
      attachmentEmergenceEvents: attachments.map((fileName) => ({
        fileName,
        emergedAtHopOrStepLabel: 'Marco #1 da cadeia (Draft 1 to site approval)',
        emergedAtTimestampLabel: '28/09/2026',
        introducedByActor: 'Sanofi / ROCK FORWARD (EFC22965)',
        relayHistorySummary: `Surgiu em 28/09/2026 no envio ao Site 0760005 → Repassado (Fwd:) em ${receivedLabel} por Elessandra Bitencourt para contratos@giyus.com.br`,
      })),
    };
  }

  // 5. SOLAIR PI Nelson Rosario reply quoting previous email from Contratos <contratos@giyus.com.br> on 21/10/2026 00:40
  if (
    upperSubj.includes('SOLAIR') &&
    msg.senderEmail.toLowerCase().includes('nelson.rosario') &&
    upperBody.includes('ENVIADO: QUARTA-FEIRA, 21 DE OUTUBRO DE 2026 00:40')
  ) {
    return {
      messageId: msg.id,
      overallTransmissionKind: 'REPLY_CHAIN',
      overallTransmissionLabel:
        '↩️ Resposta (Re:) sobre Envio Anterior de contratos@giyus.com.br (Sem Anexos)',
      hops: [
        {
          hopNumber: 1,
          actionType: 'SENT_OUTBOX',
          actionBadgeLabel: '1º Marco · 📤 Enviado por contratos@giyus.com.br',
          timestampLabel: '21/10/2026 00:40',
          fromActor: 'Contratos Giyus <contratos@giyus.com.br>',
          toRecipients:
            'Patricia.Stein@parexel.com, Dr. Nelson Augusto Rosario Filho <nelson.rosario@ufpr.br>',
          ccRecipients: null,
          summaryText:
            'Envio da versão revisada da proposta/contrato do estudo SOLAIR (D7820C00004 · Site 0701).',
          newlyEmergedAttachments: [],
          repassedAttachments: [],
        },
        {
          hopNumber: 2,
          actionType: 'REPLIED',
          actionBadgeLabel: '2º Marco · ↩️ Resposta do Investigador Principal (PI)',
          timestampLabel: receivedLabel,
          fromActor: `${msg.senderName} <${msg.senderEmail}>`,
          toRecipients: toDisplay,
          ccRecipients: ccDisplay,
          summaryText: msg.bodyPreview,
          newlyEmergedAttachments: [],
          repassedAttachments: [],
        },
      ],
      attachmentEmergenceEvents: [],
    };
  }

  // 6. Generic Forwarded (Fwd: / FW: / ENC:), Outbox Sent, Reply (Re: / RES:), or Direct Sent
  const isOutbox =
    msg.mailboxFolder === 'OUTBOX' ||
    (msg.senderEmail.toLowerCase().endsWith('@giyus.com.br') &&
      (upperSubj.startsWith('RE:') || upperSubj.startsWith('RES:')));
  const isForward =
    upperSubj.startsWith('FWD:') || upperSubj.startsWith('FW:') || upperSubj.startsWith('ENC:');
  const isReply = upperSubj.startsWith('RE:') || upperSubj.startsWith('RES:');

  if (isForward) {
    return {
      messageId: msg.id,
      overallTransmissionKind: 'FORWARDED_CHAIN',
      overallTransmissionLabel:
        attachments.length > 0
          ? `↪️ E-mail Repassado (Fwd:/ENC:) c/ ${attachments.length} anexo(s) trazido(s) do envio anterior`
          : '↪️ E-mail Repassado / Encaminhado (Fwd:/ENC:) sem anexos',
      hops: [
        {
          hopNumber: 1,
          actionType: attachments.length > 0 ? 'ATTACHMENT_INTRODUCED' : 'ORIGIN_SENT',
          actionBadgeLabel:
            attachments.length > 0
              ? '1º Marco · 📎 Envio Original + Surgimento do(s) Anexo(s)'
              : '1º Marco · 📨 Envio Original na Conversa',
          timestampLabel: 'Envio Original Anterior ao Repasse',
          fromActor: 'Remetente Original da Conversa Externa',
          toRecipients: `${msg.senderName} <${msg.senderEmail}>`,
          ccRecipients: null,
          summaryText:
            attachments.length > 0
              ? `Momento em que ${attachments.length} anexo(s) (${attachments.join(', ')}) surgiu(ram) originalmente antes do encaminhamento.`
              : 'Mensagem original enviada antes de ser repassada para a caixa compartilhada.',
          newlyEmergedAttachments: attachments,
          repassedAttachments: [],
        },
        {
          hopNumber: 2,
          actionType: 'FORWARDED_RELAY',
          actionBadgeLabel: '2º Marco · ↪️ Repassado (Fwd:) p/ Caixa Compartilhada',
          timestampLabel: receivedLabel,
          fromActor: `${msg.senderName} <${msg.senderEmail}>`,
          toRecipients: toDisplay,
          ccRecipients: ccDisplay,
          summaryText: msg.bodyPreview,
          newlyEmergedAttachments: [],
          repassedAttachments: attachments,
        },
      ],
      attachmentEmergenceEvents: attachments.map((fileName) => ({
        fileName,
        emergedAtHopOrStepLabel: 'Marco #1 (Envio Original antes do Fwd:)',
        emergedAtTimestampLabel: `Antes de ${receivedLabel}`,
        introducedByActor: `Origem repassada por ${msg.senderName} <${msg.senderEmail}>`,
        relayHistorySummary: `Surgiu no envio original e foi repassado (Fwd:) em ${receivedLabel} por ${msg.senderName} para ${toDisplay}`,
      })),
    };
  }

  return {
    messageId: msg.id,
    overallTransmissionKind: isOutbox ? 'OUTBOX_SENT' : isReply ? 'REPLY_CHAIN' : 'DIRECT_SENT',
    overallTransmissionLabel: isOutbox
      ? '📤 E-mail Enviado pela Caixa de Saída (SentItems)'
      : isReply
        ? attachments.length > 0
          ? `↩️ Resposta Recebida (Re:) — 🆕 ${attachments.length} anexo(s) surgiu(ram) neste envio`
          : '↩️ Resposta Recebida (Re:) na Conversa (Sem Novos Anexos)'
        : attachments.length > 0
          ? `📨 Envio Direto Recebido — 🆕 ${attachments.length} anexo(s) surgiu(ram) neste envio`
          : '📨 Envio Direto Recebido (Sem Anexos)',
    hops: [
      {
        hopNumber: 1,
        actionType: isOutbox
          ? 'SENT_OUTBOX'
          : attachments.length > 0
            ? 'ATTACHMENT_INTRODUCED'
            : isReply
              ? 'REPLIED'
              : 'ORIGIN_SENT',
        actionBadgeLabel: isOutbox
          ? '📤 Enviado pela Equipe Giyus (Caixa de Saída)'
          : attachments.length > 0
            ? '📎 Envio Direto + SURGIMENTO DE ANEXO(S) NESTE MOMENTO'
            : isReply
              ? '↩️ Resposta Direta na Thread (Sem Novos Anexos)'
              : '📨 Envio Inicial Direto',
        timestampLabel: receivedLabel,
        fromActor: `${msg.senderName} <${msg.senderEmail}>`,
        toRecipients: toDisplay,
        ccRecipients: ccDisplay,
        summaryText: msg.bodyPreview,
        newlyEmergedAttachments: attachments,
        repassedAttachments: [],
      },
    ],
    attachmentEmergenceEvents: attachments.map((fileName) => ({
      fileName,
      emergedAtHopOrStepLabel: 'Surgiu diretamente neste envio',
      emergedAtTimestampLabel: receivedLabel,
      introducedByActor: `${msg.senderName} <${msg.senderEmail}>`,
      relayHistorySummary: `Surgiu neste e-mail em ${receivedLabel}, enviado diretamente por ${msg.senderName} <${msg.senderEmail}> para ${toDisplay}`,
    })),
  };
}
